/**
 * Login / Signup page component
 */
import { BaseElement } from '../base-element';
import { Signal } from 'signal-polyfill';
import { authActions } from '../../state/auth.state';
import { navigate, setReturnUrl } from '../../router';

type AuthMode = 'login' | 'signup';
type ProviderName = 'microsoft' | 'google' | 'apple' | 'facebook';

interface InviteContext {
  trialName: string;
  role: string;
}

export class LoginPage extends BaseElement {
  private mode = new Signal.State<AuthMode>('login');
  private email = new Signal.State('');
  private loading = new Signal.State(false);
  private inviteContext = new Signal.State<InviteContext | null>(null);
  private loadingAction: ProviderName | 'email' | null = null;
  private inviteToken: string | null = null;

  connectedCallback(): void {
    this.parseQueryParams();
    this.watch(this.mode, () => this.updateRender());
    this.watch(this.email, () => this.updateRender());
    this.watch(this.loading, () => this.updateRender());
    this.watch(this.inviteContext, () => this.updateRender());
    this.updateRender();
  }

  private parseQueryParams(): void {
    const params = new URLSearchParams(window.location.search);
    const mode = params.get('mode');
    const returnUrl = params.get('returnUrl');
    const invite = params.get('invite');

    if (mode === 'signup') {
      this.mode.set('signup');
    }

    if (returnUrl) {
      setReturnUrl(returnUrl);
    }

    if (invite) {
      this.inviteToken = invite;
      this.mode.set('signup');
      this.inviteContext.set(this.parseInviteToken(invite));
    }
  }

  private parseInviteToken(token: string): InviteContext | null {
    try {
      const decoded = atob(token);
      const parsed = JSON.parse(decoded) as Partial<InviteContext>;
      if (parsed.trialName && parsed.role) {
        return { trialName: parsed.trialName, role: parsed.role };
      }
    } catch {
      // Ignore malformed invite tokens
    }

    const [trialName, role] = token.split(':');
    if (trialName && role) {
      return { trialName, role };
    }

    return { trialName: token, role: 'Invitee' };
  }

  private updateRender(): void {
    const mode = this.mode.get();
    const isSignup = mode === 'signup';
    const email = this.email.get();
    const loading = this.loading.get();
    const inviteContext = this.inviteContext.get();
    const actionLabel = isSignup ? 'Sign up with' : 'Continue with';
    const emailActionLabel = isSignup ? 'Sign up with Email' : 'Continue with Email';

    this.render(`
      <div class="min-h-screen flex flex-col items-center justify-center bg-neutral-50 dark:bg-neutral-900 px-4" data-testid="login-page">
        <div class="w-full max-w-md bg-white dark:bg-neutral-800 rounded-2xl shadow-lg p-8">
          <div class="mb-6 text-center">
            <img src="/brand/logo-primary-light.svg" alt="Venom Dog Sports" class="mx-auto h-10 dark:hidden" />
            <img src="/brand/logo-primary-dark.svg" alt="Venom Dog Sports" class="hidden mx-auto h-10 dark:block" />
          </div>
          <h1 class="mb-4 text-center text-2xl font-semibold text-neutral-900 dark:text-white" data-testid="auth-heading">
            ${isSignup ? 'Create your account' : 'Log in to your account'}
          </h1>
          ${
            inviteContext && isSignup
              ? `
                <div class="mb-6 rounded-xl border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-700 dark:border-primary-400/30 dark:bg-primary-500/10 dark:text-primary-200" data-testid="invite-context">
                  <div class="font-semibold">You're invited to join ${this.esc(inviteContext.trialName)}</div>
                  <div class="text-xs">Role: ${this.esc(inviteContext.role)}</div>
                </div>
              `
              : ''
          }
          <div class="flex flex-col gap-3" data-testid="provider-buttons">
            ${this.renderProviderButton('microsoft', 'Microsoft', 'bg-[#2F2F2F] text-white', actionLabel)}
            ${this.renderProviderButton('google', 'Google', 'bg-white text-[#1F1F1F] border border-neutral-300 dark:border-neutral-700', actionLabel)}
            ${this.renderProviderButton('apple', 'Apple', 'bg-black text-white', actionLabel)}
            ${this.renderProviderButton('facebook', 'Facebook', 'bg-[#1877F2] text-white', actionLabel)}
          </div>

          <div class="my-6 flex items-center gap-4" data-testid="divider">
            <div class="flex-1 border-t border-neutral-200 dark:border-neutral-700"></div>
            <span class="text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400">or</span>
            <div class="flex-1 border-t border-neutral-200 dark:border-neutral-700"></div>
          </div>

          <div class="flex flex-col gap-3">
            <label class="text-sm font-medium text-neutral-700 dark:text-neutral-300" for="email-input">Email address</label>
            <input
              id="email-input"
              class="w-full rounded-lg border border-neutral-300 bg-white px-4 py-3 text-sm text-neutral-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 dark:border-neutral-700 dark:bg-neutral-900 dark:text-white dark:focus:ring-primary-500/30"
              type="email"
              placeholder="you@example.com"
              value="${this.esc(email)}"
              ${loading ? 'disabled' : ''}
              data-testid="email-input"
            />
            <button
              type="button"
              class="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary-500 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
              data-testid="email-continue-btn"
              ${loading ? 'disabled' : ''}
            >
              ${this.loadingAction === 'email' && loading ? this.renderSpinner() : ''}
              <span>${emailActionLabel}</span>
            </button>
          </div>

          <div class="mt-6 text-center text-sm text-neutral-600 dark:text-neutral-400" data-testid="mode-toggle">
            ${isSignup ? 'Already have an account?' : "Don't have an account?"}
            <button type="button" class="ml-1 text-primary-600 hover:underline" data-testid="mode-toggle-link">
              ${isSignup ? 'Log in' : 'Sign up'}
            </button>
          </div>

          <p class="mt-6 text-center text-xs text-neutral-500 dark:text-neutral-400" data-testid="legal-footer">
            By continuing, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    `);

    this.attachEventListeners();
  }

  private renderProviderButton(
    provider: ProviderName,
    label: string,
    classes: string,
    actionLabel: string
  ): string {
    const loading = this.loading.get();
    const isLoading = loading && this.loadingAction === provider;
    const icon = isLoading ? this.renderSpinner() : this.renderProviderIcon(provider);

    return `
      <button
        type="button"
        class="w-full rounded-lg py-3 px-4 font-medium flex items-center gap-3 ${classes} transition-colors disabled:cursor-not-allowed disabled:opacity-70"
        data-testid="provider-btn-${provider}"
        data-provider="${provider}"
        ${loading ? 'disabled' : ''}
      >
        <span class="flex h-5 w-5 items-center justify-center">${icon}</span>
        <span>${actionLabel} ${label}</span>
      </button>
    `;
  }

  private renderProviderIcon(provider: ProviderName): string {
    switch (provider) {
      case 'microsoft':
        return `
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="h-5 w-5">
            <path d="M3 3h8v8H3z" fill="currentColor"/>
            <path d="M13 3h8v8h-8z" fill="currentColor"/>
            <path d="M3 13h8v8H3z" fill="currentColor"/>
            <path d="M13 13h8v8h-8z" fill="currentColor"/>
          </svg>
        `;
      case 'google':
        return `
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="h-5 w-5">
            <path d="M12 4a8 8 0 015.657 2.343l-2.1 2.1A5 5 0 0012 7a5 5 0 000 10 4.5 4.5 0 004.314-3H12v-3h7a8 8 0 01-7 9 8 8 0 110-16z" fill="currentColor"/>
          </svg>
        `;
      case 'apple':
        return `
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="h-5 w-5">
            <path d="M16.5 7.2c-1.1 0-2.3.7-3 1.8-.6 1-.9 2.2-.8 3.4.1 2.3 1.6 4.7 4.1 4.7 1.2 0 1.7-.5 3-.5 1.3 0 1.7.5 3 .5 1.3 0 2.2-1.1 2.8-2.1-.1-.1-2.5-1-2.5-3.9 0-2.5 2-3.6 2.1-3.7-1.2-1.7-3-1.9-3.6-1.9-1.4-.1-2.6.7-3.3.7-.7 0-1.7-.7-2.8-.7z" fill="currentColor"/>
            <path d="M15.6 2.5c-.5.6-.9 1.5-.8 2.4 1 .1 2-.5 2.6-1.2.5-.6.9-1.5.8-2.4-1-.1-2 .5-2.6 1.2z" fill="currentColor"/>
          </svg>
        `;
      case 'facebook':
        return `
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="h-5 w-5">
            <path d="M14.5 8.5V7c0-1.1.7-1.3 1.2-1.3h1.8V2.1L15 2c-2.9 0-3.6 2.2-3.6 3.6v2H9v3.2h2.4V22h3.1v-11h2.6l.4-3.2h-3z" fill="currentColor"/>
          </svg>
        `;
    }
  }

  private renderSpinner(): string {
    return `<dt-spinner size="small"></dt-spinner>`;
  }

  private attachEventListeners(): void {
    const providerButtons = this.$$('[data-provider]');
    providerButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        const target = event.currentTarget as HTMLElement;
        const provider = target.getAttribute('data-provider') as ProviderName | null;
        if (provider) {
          this.loadingAction = provider;
          this.loading.set(true);
          authActions.loginWithProvider(provider, this.mode.get());
        }
      });
    });

    const emailInput = this.$('[data-testid="email-input"]') as HTMLInputElement | null;
    if (emailInput) {
      emailInput.addEventListener('input', (event) => {
        const target = event.target as HTMLInputElement;
        this.email.set(target.value);
      });
    }

    const emailButton = this.$('[data-testid="email-continue-btn"]');
    if (emailButton) {
      emailButton.addEventListener('click', () => {
        this.loadingAction = 'email';
        this.loading.set(true);
        authActions.loginWithEmail(this.email.get(), this.mode.get());
      });
    }

    const modeToggle = this.$('[data-testid="mode-toggle-link"]');
    if (modeToggle) {
      modeToggle.addEventListener('click', () => this.handleModeToggle());
    }
  }

  private handleModeToggle(): void {
    const nextMode: AuthMode = this.mode.get() === 'login' ? 'signup' : 'login';
    this.mode.set(nextMode);

    const params = new URLSearchParams(window.location.search);
    if (nextMode === 'signup') {
      params.set('mode', 'signup');
    } else {
      params.delete('mode');
    }

    const returnUrl = sessionStorage.getItem('dt-return-url');
    if (returnUrl) {
      params.set('returnUrl', returnUrl);
    }

    if (this.inviteToken) {
      params.set('invite', this.inviteToken);
    }

    const query = params.toString();
    navigate(`/login${query ? `?${query}` : ''}`, { replace: true });
  }
}

customElements.define('dt-login-page', LoginPage);
